import { Drawer, resolvePhoneNumberFormat } from '@aspendental/shared-components-web';
import { Form, SchemaBuilder, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import { Alert, Box, Button, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { facilitiesStyles } from './FindAntherOffice.styles';
import AddressBlock from '@src/components/DateAndTime/Location/AddressBlock';
import DistanceIndicator from '@src/components/DateAndTime/Location/DistanceIndicator';
import { useGeolocation } from '@src/hooks/useGeolocation';
import { OfficeSearchApiResponse } from '@src/lib/__generated/api';
import GetCoordinates from '@src/services/coordinates';
import { PatientInformationSchema, usePatientInformationFormStore } from '@src/store';
export default function FindAnotherOffice({
  setOpenDrawer,
  openDrawer,
  facilityLocations,
  setFacility
}: Readonly<{
  setOpenDrawer: (open: boolean) => void;
  openDrawer: boolean;
  facilityLocations?: OfficeSearchApiResponse[];
  setFacility: (facility: OfficeSearchApiResponse) => void;
}>) {
  const [locationLookupError, setLocationLookupError] = useState(false);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const {
    t
  } = useTranslation('dateAndTime');
  const geolocationService = useGeolocation();
  const setGeolocation = usePatientInformationFormStore(store => store.setGeolocation);
  const handleUseCurrentLocation = () => {
    if (geolocationService.latitude && geolocationService.longitude) {
      setGeolocation(geolocationService.latitude, geolocationService.longitude);
    }
  };
  const handleSubmit = async (values: FieldValues) => {
    try {
      const coords = await GetCoordinates(values.zipCode);
      const coordinates = coords;
      setGeolocation(coordinates.lat, coordinates.lng);
      setLocationLookupError(false);
    } catch (e) {
      console.error(e);
      setLocationLookupError(true);
    }
  };
  const schema = SchemaBuilder([{
    name: 'zipCode',
    validation: PatientInformationSchema.zipCode
  }]);
  const defaultValues = {
    zipCode: ''
  };
  const form = useHookForm({
    schema,
    defaultValues
  });
  return <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)} width={isLargeScreen ? '50%' : '100%'} closeButton data-sentry-element="Drawer" data-sentry-component="FindAnotherOffice" data-sentry-source-file="FindAnotherOffice.tsx">
			<Grid container direction="column" spacing={2} sx={{
      padding: '2rem'
    }} data-sentry-element="Grid" data-sentry-source-file="FindAnotherOffice.tsx">
				<Grid item data-sentry-element="Grid" data-sentry-source-file="FindAnotherOffice.tsx">
					<Typography variant="header3" gutterBottom color="primary" data-sentry-element="Typography" data-sentry-source-file="FindAnotherOffice.tsx">
						{t('findAnOffice')}
					</Typography>
				</Grid>
				<Grid item data-sentry-element="Grid" data-sentry-source-file="FindAnotherOffice.tsx">
					<Typography variant="bodyMediumBook" gutterBottom color="primary" data-sentry-element="Typography" data-sentry-source-file="FindAnotherOffice.tsx">
						{t('enterZIPToFind')}
					</Typography>
				</Grid>

				<Grid item data-sentry-element="Grid" data-sentry-source-file="FindAnotherOffice.tsx">
					<Form form={form as any} onSubmit={handleSubmit} data-sentry-element="Form" data-sentry-source-file="FindAnotherOffice.tsx">
						<Grid container data-sentry-element="Grid" data-sentry-source-file="FindAnotherOffice.tsx">
							<Grid item sx={{
              width: '10rem'
            }} data-sentry-element="Grid" data-sentry-source-file="FindAnotherOffice.tsx">
								<TextInput name="zipCode" label={t('zipCodeLabel')} maxLength={5} required={false} data-sentry-element="TextInput" data-sentry-source-file="FindAnotherOffice.tsx" />
							</Grid>
							<Grid item data-sentry-element="Grid" data-sentry-source-file="FindAnotherOffice.tsx">
								<Button type="submit" variant="tertiaryDefault" size="M" data-sentry-element="Button" data-sentry-source-file="FindAnotherOffice.tsx">
									{t('search')}
								</Button>
							</Grid>
						</Grid>
					</Form>
				</Grid>
				<Grid item data-sentry-element="Grid" data-sentry-source-file="FindAnotherOffice.tsx">
					<Button data-test-id="use-my-location-button" variant="tertiaryDefault" size="M" onClick={handleUseCurrentLocation} data-sentry-element="Button" data-sentry-source-file="FindAnotherOffice.tsx">
						{t('useCurrentLocation')}
					</Button>
				</Grid>
				{locationLookupError && <Grid item>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('couldNotDetermine')}</Typography>
						</Alert>
					</Grid>}
			</Grid>

			{facilityLocations?.map(location => {
      return <Box mx={2} mb={4} pb={2} component={Button} sx={facilitiesStyles} key={location.facilityCode} onClick={() => {
        setFacility(location);
        setOpenDrawer(false);
      }}>
						<Stack direction="row" justifyContent="space-between">
							<Typography variant={isLargeScreen ? 'header4' : 'bodyLargeSemiBold'}>
								{location.title}
							</Typography>
							<DistanceIndicator distance={location.distance} />
						</Stack>
						<Stack alignItems="flex-start">
							<AddressBlock address={location.address} zipCode={location.zipCode} />
							<Typography variant="bodyMediumBook" color="text.primary">
								{resolvePhoneNumberFormat(location.phone ?? '')}
							</Typography>
						</Stack>
					</Box>;
    })}
		</Drawer>;
}